import React, { FC, ReactElement, useCallback, useMemo } from 'react'
import { PartitionModal } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import FormRow from '../../Common/Components/FormRow'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'
import { OutlinedSelect } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedSelect'
import { PartitionModalSyncLayout, PartitionModalTelSysLayout } from '../Utils/partitionModalTelSysLayout'
import {
    PARTITION_COUNTRY_CODE_FIELD_NAME,
    PARTITION_E164_ENABLED_FIELD_NAME,
    PARTITION_INTERNAL_DIGIT_LEN_FIELD_NAME,
    PARTITION_RECORDING_PLAYBACK_FIELD_NAME,
    PARTITION_START_OF_DAY_FIELD_NAME,
    PARTITION_TIMEZONE_FIELD_NAME
} from '../Constants/FieldNames'
import { RecordingIntegrations } from '../../Common/Constants/Recordings'
import { SelectOption } from '../../Common/Components/Selects/Types'
import { getCallRecordingIntegration } from '../../../NonReact/common/API/EntityAPIClient'
import { CallRecordingIntegration } from '../../../NonReact/Types/Types'
import { UsePromiseValueResult, usePromiseValue } from '../../Common/Hooks/usePromiseValue'
import { getTimezoneOptions } from '../../Common/API/UtilitiesAPI'

type Props = {
    viewOnly?: boolean
    telSysLayout: PartitionModalTelSysLayout
    telSysID: number
}

/**
 * Site settings tab (timezone, recording, digit length, country) with TelSys-specific fields.
 *
 * @param props - Tab props.
 * @returns Site settings tab content.
 */
export const PartitionSiteSettingsTab: FC<Props> = ({ viewOnly, telSysLayout, telSysID }: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    const disabled: boolean = viewOnly ?? false
    const showE164Enabled: boolean =
        telSysLayout.syncLayout !== PartitionModalSyncLayout.BROADWORKS &&
        telSysLayout.syncLayout !== PartitionModalSyncLayout.WEBEX

    const noneRecordingOption: SelectOption<string> = useMemo(
        (): SelectOption<string> => ({
            label: language.get(PartitionModal.PartitionModal_Site_RecordingPlayback_None),
            value: RecordingIntegrations.NONE
        }),
        [language]
    )
    const fetchCallRecordingIntegrations = useCallback(
        (): Promise<Nullable<CallRecordingIntegration[]>> => getCallRecordingIntegration(telSysID),
        [telSysID]
    )

    const { value: recordingIntegrations }: UsePromiseValueResult<Nullable<CallRecordingIntegration[]>> =
        usePromiseValue<Nullable<CallRecordingIntegration[]>>(
            fetchCallRecordingIntegrations,
            {
                initialValue: null,
                enabled: telSysLayout.siteShowRecordingPlayback
            },
            [telSysID, telSysLayout.siteShowRecordingPlayback]
        )
    const { value: availableTimezones }: UsePromiseValueResult<SelectOption<string>[]> = usePromiseValue<
        SelectOption<string>[]
    >(
        getTimezoneOptions,
        {
            initialValue: [],
            enabled: true
        },
        []
    )

    const recordingPlaybackOptions: SelectOption<string>[] = useMemo(
        (): SelectOption<string>[] => [
            noneRecordingOption,
            ...(recordingIntegrations?.map(
                (recordingIntegration: CallRecordingIntegration): SelectOption<string> => ({
                    label: recordingIntegration.Description,
                    value: recordingIntegration.ID
                })
            ) ?? [])
        ],
        [noneRecordingOption, recordingIntegrations]
    )

    return (
        <div className='partition-config-tab'>
            <FormRow>
                <OutlinedSelect
                    name={PARTITION_TIMEZONE_FIELD_NAME}
                    label={language.get(PartitionModal.PartitionModal_Site_Timezone)}
                    options={availableTimezones}
                    disabled={disabled}
                />
            </FormRow>
            <FormRow>
                <OutlinedTextInput
                    labelLeft={language.get(PartitionModal.PartitionModal_Site_StartOfDay)}
                    inputName={PARTITION_START_OF_DAY_FIELD_NAME}
                    disabled={disabled}
                />
            </FormRow>
            {telSysLayout.siteShowRecordingPlayback ? (
                <FormRow>
                    <OutlinedSelect
                        name={PARTITION_RECORDING_PLAYBACK_FIELD_NAME}
                        label={language.get(PartitionModal.PartitionModal_Site_RecordingPlayback)}
                        options={recordingPlaybackOptions}
                        disabled={disabled}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {showE164Enabled ? (
                <FormRow>
                    <RouterCheckbox
                        labelLeft={language.get(PartitionModal.PartitionModal_Site_E164Enabled)}
                        name={PARTITION_E164_ENABLED_FIELD_NAME}
                        disabled={disabled}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {telSysLayout.siteShowInternalDigitLength ? (
                <FormRow>
                    <OutlinedTextInput
                        labelLeft={language.get(PartitionModal.PartitionModal_Site_InternalDigitLength)}
                        inputName={PARTITION_INTERNAL_DIGIT_LEN_FIELD_NAME}
                        disabled={disabled}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            {telSysLayout.siteShowCountryCode ? (
                <FormRow>
                    <OutlinedTextInput
                        labelLeft={language.get(PartitionModal.PartitionModal_Site_CountryCodeLocation)}
                        inputName={PARTITION_COUNTRY_CODE_FIELD_NAME}
                        disabled={disabled}
                    />
                </FormRow>
            ) : (
                <></>
            )}
        </div>
    )
}
